import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./dashboard.scss";
import BreadCrumsHeader from "../BreadCrums";
import Box from "./Box";
import MerchantIcon from "../../../Assets/Icons/merchantIcon.svg";
import FundraiserIcon from "../../../Assets/Icons/FundraiserIcon.svg";
import MembershipIcon from "../../../Assets/Icons/membershipIcon.svg";
import CustomerIcon from "../../../Assets/Icons/customerIcon.svg";
import OfferIcon from "../../../Assets/Icons/offerIcon.svg";
import Summary from "./Summary";
// import MerchantOffer from "./MerchantOffer";
import { useDispatch, useSelector } from "react-redux";
import { roleEnum } from "../../../utils/utilFunctions";
import { getOnboardingStatus, getFundraiserProfile } from "../../../services/fundraiserService";
import { logout } from "../../../services/authService";
import { getFcmToken } from "../../../utils/connectStore";
import { getAdminFundStats, getAdminFundBreakdown, getAdminOfferStats, getMerchantStatusSummary, getFundraiserStatusSummary } from "../../../services/adminService";
import AdminDetailDashboard from "./AdminDetailDashboard";
import FundraiserDashboard from "./FundraiserDashboard";
import Frequency from "./Frequency";
import NonRenewedSubscriptions from "./NonRenewedSubscriptions";
import UpcomingRenewals from "./UpcomingRenewals";
import { getDashboardStats } from "../../../services/subscriptionService";
import FundsChart from "./FundsChart";
import FundsTable from "./FundsTable";
import ActiveInactiveChart from "./ActiveInactiveChart";
import Loader from "../Loader";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  // const fundraiserLoading = useSelector((state) => state.fundraiserReducer.loading);
  const { merchantDashboardStats, selectedMerchent } = useSelector(
    (state) => state?.merchantReducer
  );
  const userData = useSelector((state) => state?.authReducer?.tokenData?.user);
  const userRole = (userData?.role || "").toString().toLowerCase();
  const location = useLocation();
  const currentPath = location.pathname;
  // Start with checking=true for fundraisers to prevent dashboard flash
  const [isCheckingStatus, setIsCheckingStatus] = useState(userRole === "fundraiser" && currentPath.includes(roleEnum.fundraiser));

  console.log("new", merchantDashboardStats);
  const merchatCardsData = merchantDashboardStats?.data;
  const [selectedFrequency, setSelectedFrequency] = useState("Monthly");
  const [freqLoading, setFreqLoading] = useState(false);
  const merchantStatusSummary = useSelector((s) => s?.adminReducer?.merchantStatusSummary);
  const fundraiserStatusSummary = useSelector((s) => s?.adminReducer?.fundraiserStatusSummary);

  // Guard: check fundraiser approval status BEFORE any rendering
  useEffect(() => {
    const checkFundraiserStatus = async () => {
      if (userRole === "fundraiser" && currentPath.includes(roleEnum.fundraiser)) {
        // Keep checking state true until verification complete
        try {
          const profileResponse = await getFundraiserProfile();
          const fundraiserProfile = profileResponse?.data;
          const profileStatus = typeof fundraiserProfile?.status === "string"
            ? fundraiserProfile.status.toLowerCase()
            : fundraiserProfile?.status;
          const isApproved = profileStatus === "approved";

          if (!isApproved) {
            // NOT APPROVED - set flag for modal and logout
            // Keep loader showing during entire logout process
            if (profileStatus !== 'declined') {
              // Set flag to show modal on login page (only for pending, not declined)
              sessionStorage.setItem('showAccountPendingModal', 'true');
            }
            await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
            const msg = profileStatus === 'declined'
              ? 'Your account has been declined. Please contact support.'
              : 'Your account is pending admin approval. Please wait for approval.';
            navigate('/fundraiser/account-login', { replace: true, state: { message: msg } });
            return;
          }
          // Approved - allow dashboard to render
          setIsCheckingStatus(false);
        } catch (profileError) {
          console.error("Failed to fetch fundraiser profile:", profileError);
          await dispatch(logout({ fcmToken: getFcmToken() }))?.unwrap();
          navigate('/fundraiser/account-login', { replace: true, state: { message: 'Failed to verify account status. Please try again.' } });
          return;
        }
      } else {
        // Not fundraiser or not fundraiser path - don't check
        setIsCheckingStatus(false);
      }
    };
    checkFundraiserStatus();
  }, [userData, currentPath, dispatch, navigate, userRole]);

  const getSeries = (obj) => (Array.isArray(obj) ? obj : null);
  const normalize12 = (arr) => {
    if (!arr) return undefined;
    const nums = arr.map((n) => Number(n) || 0);
    return nums.length === 12 ? nums : [...nums, ...new Array(12 - nums.length).fill(0)].slice(0, 12);
  };

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const monthKeyMap = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11
  };

  const buildSeriesFromStatus = (statusObj) => {
    const s = statusObj || {};
    // Direct arrays
    const activeArr = getSeries(s?.active) || getSeries(s?.monthly?.active) || getSeries(s?.perMonth?.active);
    const inactiveArr = getSeries(s?.inactive) || getSeries(s?.monthly?.inactive) || getSeries(s?.perMonth?.inactive);
    if (activeArr || inactiveArr) {
      return {
        active: normalize12(activeArr || Array(12).fill(0)),
        inactive: normalize12(inactiveArr || Array(12).fill(0))?.map((v) => -Math.abs(v)),
      };
    }

    // Monthly object keyed by month name → counts
    if (s?.monthly && typeof s.monthly === 'object' && !Array.isArray(s.monthly)) {
      const a = Array(12).fill(0);
      const i = Array(12).fill(0);
      Object.entries(s.monthly).forEach(([k, v]) => {
        const idx = monthKeyMap[String(k).toLowerCase()];
        if (idx != null) {
          a[idx] = Number(v?.activeMerchants) || 0;
          i[idx] = -Math.abs(Number(v?.inactiveMerchants) || 0);
        }
      });
      return { active: a, inactive: i };
    }

    // Monthly array of objects [{ month: 'August', activeMerchants, inactiveMerchants }, ...]
    if (Array.isArray(s?.monthly)) {
      const a = Array(12).fill(0);
      const i = Array(12).fill(0);
      s.monthly.forEach((row) => {
        const idx = monthKeyMap[String(row?.month).toLowerCase()];
        if (idx != null) {
          a[idx] = Number(row?.activeMerchants) || 0;
          i[idx] = -Math.abs(Number(row?.inactiveMerchants) || 0);
        }
      });
      return { active: a, inactive: i };
    }

    // Weekly object: sum and place at last column
    if (s?.weekly && typeof s.weekly === 'object') {
      let active = 0, inactive = 0;
      Object.values(s.weekly).forEach((v) => {
        active += Number(v?.activeMerchants) || 0;
        inactive += Number(v?.inactiveMerchants) || 0;
      });
      const a = Array(12).fill(0); a[11] = active;
      const i = Array(12).fill(0); i[11] = -Math.abs(inactive);
      return { active: a, inactive: i };
    }

    // Today only: place at last column
    if (s?.today) {
      const a = Array(12).fill(0); a[11] = Number(s.today?.activeMerchants) || 0;
      const i = Array(12).fill(0); i[11] = -Math.abs(Number(s.today?.inactiveMerchants) || 0);
      return { active: a, inactive: i };
    }

    return { active: Array(12).fill(0), inactive: Array(12).fill(0) };
  };

  const merchantsActiveInactive = buildSeriesFromStatus(merchantStatusSummary);

  const fundraisersActiveInactive = buildSeriesFromStatus(fundraiserStatusSummary);

  // Consolidated dashboard fetch with single in-flight guard
  const inFlightAllRef = React.useRef(false);
  const fetchAll = useCallback(async (timeframe) => {
    if (inFlightAllRef.current) return;
    inFlightAllRef.current = true;
    setFreqLoading(true);
    try {
      const tf = timeframe || undefined;
      const [statsRes] = await Promise.all([
        dispatch(getDashboardStats(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getAdminOfferStats(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getAdminFundStats(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getAdminFundBreakdown(tf ? { timeframe: tf } : {}))?.unwrap(),
        dispatch(getMerchantStatusSummary())?.unwrap(),
        dispatch(getFundraiserStatusSummary())?.unwrap(),
      ]);
      if (statsRes?.success) setAdminStats(statsRes?.data);
    } catch (_) { }
    inFlightAllRef.current = false;
    setFreqLoading(false);
  }, [dispatch]);

  // Load admin dashboard data (single consolidated call)
  useEffect(() => {
    if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
      const tf = selectedFrequency?.toLowerCase?.();
      fetchAll(tf);
    }
  }, [currentPath, selectedFrequency, fetchAll]);

  // Listen for offer creation/updates to refresh dashboard data
  const createdOffer = useSelector((state) => state?.offerReducer?.createdOffer);
  const updatedOffer = useSelector((state) => state?.offerReducer?.updatedOffer);

  useEffect(() => {
    if (createdOffer || updatedOffer) {
      if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
        const tf = selectedFrequency?.toLowerCase?.();
        fetchAll(tf);
      }
    }
  }, [createdOffer, updatedOffer, currentPath, selectedFrequency, fetchAll]);

  const items = [
    {
      title: "Welcome to Swoodle",
    },
  ];
  // Admin summary cards — ordered and named to match the provided design
  const [adminStats, setAdminStats] = useState(null);
  const adminBoxes = [
    {
      text: "Total Subscriptions",
      number: adminStats?.subscriptions?.total ?? "-",
      icon: MembershipIcon,
    },
    {
      text: "Total Fundraisers",
      number: adminStats?.fundraisers?.total ?? "-",
      icon: FundraiserIcon,
    },
    {
      text: "Total Merchants",
      number: adminStats?.merchants?.total ?? "-",
      icon: MerchantIcon,
    },
    {
      text: "Total Offers",
      number: adminStats?.offers?.total ?? "-",
      icon: OfferIcon,
    },
  ];

  const merchantBoxes = [
    {
      // text: "Total Merchants",
      text: "Unique Users",
      number: merchatCardsData?.uniqueUsers,
      icon: MerchantIcon,
    },
    {
      // text: "Total Offers",
      text: "Total Offers",
      number: merchatCardsData?.totalOffers,
      icon: OfferIcon,
    },
    {
      // text: "Active Merchants",
      text: "Total Redemptions",
      number: merchatCardsData?.totalRedumption,
      icon: CustomerIcon,
    },
    // {
    //   text: "Inactive Merchants",
    //   text: "Total Revenue",
    //   number: merchatCardsData?.totalRevenue,
    //   icon: InactiveMerchantsIcon,
    // },
  ];

  const frequencies = [
    "Monthly",
    "Weekly",
    "Yearly",
    "Today"
  ];

  const getStatus = async () => {
    try {
      const statusPromise = await dispatch(getOnboardingStatus()).unwrap();
      const data = statusPromise?.data;

      // If we don't get valid data, don't make any navigation decisions
      if (!data) return;

      if (
        data.detailsSubmitted === true &&
        data.chargesEnabled === false
      ) {
        if (currentPath !== "/complete-setup") {
          navigate("/complete-setup");
        }
      } else if (
        data.detailsSubmitted === false &&
        data.chargesEnabled === false
      ) {
        if (currentPath !== "/fundraiser/welcome") {
          navigate("/fundraiser/welcome");
        }
      }
    } catch (error) {
      console.error("Error checking onboarding status:", error);
      // During network errors, don't redirect the user. Stay on current page.
    }
  };

  useEffect(() => {
    if (currentPath.includes(roleEnum.fundraiser)) {
      getStatus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);



  useEffect(() => {
    // Expose selected frequency globally for lightweight consumers (e.g., NewOffers tile)
    if (typeof window !== 'undefined') {
      window.__ADMIN_SELECTED_FREQUENCY__ = selectedFrequency;
    }
    if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
      const tf = selectedFrequency?.toLowerCase?.();
      fetchAll(tf);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  // Refresh dashboard data when user returns to dashboard
  useEffect(() => {
    if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
      const isReturningFromOfferFlow = sessionStorage.getItem("ADMIN_OFFER_CREATION_GUARD");
      if (isReturningFromOfferFlow) {
        sessionStorage.removeItem("ADMIN_OFFER_CREATION_GUARD");
        const tf = selectedFrequency?.toLowerCase?.();
        fetchAll(tf);
      }
    }
  }, [currentPath, selectedFrequency, fetchAll]);

  // Optional: gentle periodic refresh (5 min)
  useEffect(() => {
    if (!currentPath.includes(roleEnum.merchant) && !currentPath.includes(roleEnum.fundraiser)) {
      const refreshInterval = setInterval(() => {
        const tf = selectedFrequency?.toLowerCase?.();
        fetchAll(tf);
      }, 300000); // 5 minutes
      return () => clearInterval(refreshInterval);
    }
  }, [currentPath, selectedFrequency, fetchAll]);

  // For fundraisers: Return ONLY full-screen loader while checking status
  // This prevents sidebar/navbar from rendering (no dashboard flash)
  if (currentPath.includes(roleEnum.fundraiser) && isCheckingStatus) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {currentPath.includes(roleEnum.merchant) ? (
        <>
          <BreadCrumsHeader heading="Dashboard" items={items} form={true} />
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 w-full gap-3 my-[2%]">
            {merchantBoxes?.map((b) => (
              <Box
                text={b?.text}
                number={b?.number}
                icon={b?.icon}
                mainClassName="box p-3 h-[113px]"
                textClassName="text-[14px] font-poppins font-[600] text-[white]"
                numberClassName="text-[36px] font-[700] text-[white] font-montserrat"
              />
            ))}
          </div>
          <Summary key={selectedMerchent?._id || 'no-merchant'} />
          {/* <div className="w-full flex sm:flex-col lg:flex-row mt-16 justify-between items-stretch gap-4">
            <div className="sm:w-full lg:w-[60%]">
              <Summary />
            </div>
            <div className="sm:w-full lg:w-[40%]">
              <OffersCategory />
            </div>
          </div> */}
        </>
      ) : currentPath.includes(roleEnum.fundraiser) ? (
        // NEVER render dashboard until status check complete - prevents flash
        isCheckingStatus ? <Loader /> : <FundraiserDashboard items={items} />
      ) : (
        <>
          {freqLoading && <Loader />}
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center col-span-12 gap-4">
            <BreadCrumsHeader heading="Dashboard" items={items} form={true} />
            <Frequency
              selectedFrequency={selectedFrequency}
              setSelectedFrequency={(val) => {
                setSelectedFrequency(val);
                if (typeof window !== 'undefined') { window.__ADMIN_SELECTED_FREQUENCY__ = val; }
                const tf = val?.toLowerCase?.();
                fetchAll(tf);
              }}
              frequencies={frequencies}
              className='frequency w-full sm:w-[50%] md:w-[50%] lg:w-[40%] xl:w-[30%] py-2 px-[1%]'
            />
          </div>
          <div className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full gap-3 my-[2%]">
            {adminBoxes?.map((b) => (
              <Box
                text={b?.text}
                number={b?.number}
                icon={b?.icon}
                reverse
                mainClassName="box p-3 h-[113px]"
                textClassName="text-[14px] font-poppins font-[600] text-[white]"
                numberClassName="text-[36px] font-[700] text-[white] font-montserrat"
              />
            ))}
          </div>
          {/* In-widget loader removed as per request */}
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch gap-4 mb-8">
            <div className="sm:w-full lg:w-[49.5%]">
              <NonRenewedSubscriptions />
            </div>
            <div className="sm:w-full lg:w-[49.5%]">
              <UpcomingRenewals selectedFrequency={selectedFrequency} />
            </div>
          </div>
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch gap-4 mb-8">
            <div className="sm:w-full lg:w-[49.5%]">
              <FundsChart />
            </div>
            <div className="sm:w-full lg:w-[49.5%]">
              <FundsTable />
            </div>
          </div>
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch gap-4 mb-8">
            <div className="sm:w-full lg:w-[49.5%]">
              <ActiveInactiveChart
                title="Active & Inactive Merchants"
                viewAllLink="/merchant-list"
                colors={['#91CC75', '#00ADEF']}
                data={merchantsActiveInactive}
              />
            </div>
            <div className="sm:w-full lg:w-[49.5%]">
              <ActiveInactiveChart
                title="Active & Inactive Fundraisers"
                viewAllLink="/fundraiser-list"
                colors={['#91CC75', '#5470C6']}
                data={fundraisersActiveInactive}
              />
            </div>
          </div>
          <div className="w-full flex sm:flex-col lg:flex-row mt-8 justify-between items-stretch">
            <div className="sm:w-full lg:w-full">
              <AdminDetailDashboard />
            </div>
            {/* <div className="sm:w-full lg:w-[49.5%]">
              <MerchantOffer />
            </div> */}
          </div>
        </>
      )}
    </>
  );
};

export default AdminDashboard;
